import type { TFunction } from 'i18next';
import { DESTINATIONS } from '../vendor/command-palette/command-palette';
import { WORKSPACES, workspaceForPath } from '../vendor/side-bar/workspaces';
import type { PresenceMember } from './presence.store';

/**
 * Where someone is, in words.
 *
 * Their page's own breadcrumbs first — the most specific thing there is
 * ("Catalog › Oak plank 20×120"), and what they themselves are looking at.
 * Pages without a top bar report none; for those a vendor's path is matched
 * against the command palette's destinations, then its workspace.
 */
export function pageLabel(member: Pick<PresenceMember, 'crumbs' | 'path'>, t: TFunction, isSuperAdmin: boolean): string | null {
  if (member.crumbs?.length) return member.crumbs.slice(-2).join(' › ');
  if (!member.path || isSuperAdmin) return null;

  const path = member.path;
  let best: { labelKey: string; length: number } | null = null;
  for (const d of DESTINATIONS) {
    const route = d.route.split('?')[0];
    if (!route.startsWith('/')) continue;
    const matches = path === route || path.startsWith(`${route}/`);
    if (matches && (!best || route.length > best.length)) best = { labelKey: d.labelKey, length: route.length };
  }
  if (best) return t(best.labelKey) as string;

  const workspaceId = workspaceForPath(path);
  const workspace = workspaceId ? WORKSPACES.find((w) => w.id === workspaceId) : null;
  return workspace ? (t(workspace.labelKey, workspace.label) as string) : null;
}

/** "Active now", or how long they have been away, in the short form a tooltip has room for. */
export function statusLabel(member: Pick<PresenceMember, 'status' | 'since'>, t: TFunction, now = Date.now()): string {
  if (member.status === 'active') return t('PRESENCE.ACTIVE') as string;
  const minutes = Math.floor(Math.max(0, now - new Date(member.since).getTime()) / 60_000);
  if (minutes < 1) return t('PRESENCE.AWAY') as string;
  if (minutes < 60) return t('PRESENCE.AWAY_MINUTES', { count: minutes }) as string;
  return t('PRESENCE.AWAY_HOURS', { count: Math.floor(minutes / 60) }) as string;
}
